import { useState } from "react"
import type { ChaveConfigPontoAcesso, ConfigPontoAcessoOverride, PontoAcessoConfiguravel } from "@/api"
import { AvisoPinDispositivo } from "@/components/admin/aviso-pin-dispositivo"
import { InfoModo } from "@/components/admin/painel-info"
import { Alert, AlertTitle, Box, Button, Chip, FormControlLabel, InputAdornment, Link, MenuItem, Radio, RadioGroup, Stack, Switch, TextField, Typography } from "@mui/material"
import { BotaoGerenciarPontosDeAcesso, PontosDeAcessoCard } from "@/components/admin/pontos-de-acesso-card"
import { DialogPontosAcesso } from "@/components/admin/dialog-pontos-acesso"
import { UploadImagensTotem } from "@/components/admin/upload-imagens-totem"
import { CampoUsuarioNfce } from "@/components/admin/campo-usuario-nfce"
import { GateModo, OpcaoImpressaoTotem, Secao } from "."
import type { AcessoModos } from "@/lib/modos-cardapio"
import TableRestaurantIcon from '@mui/icons-material/TableRestaurant'
import PointOfSaleIcon from '@mui/icons-material/PointOfSale'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import SlideshowIcon from '@mui/icons-material/Slideshow'
import QrCode2Icon from '@mui/icons-material/QrCode2'
import ScaleIcon from '@mui/icons-material/Scale'
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner'
import PaidIcon from '@mui/icons-material/Paid'
import AddIcon from '@mui/icons-material/Add'
import Grid from '@mui/material/Grid2'

/** Download do aplicativo do totem — é ele que conversa com a balança. */
const URL_DOWNLOADS = 'https://downloads.sischef.com/'

interface SecaoCheckoutKioskProps {
    modos: AcessoModos | undefined
    form: Record<string, unknown>
    idConfig: number
    pontos: PontoAcessoConfiguravel[]
    idPontoSelecionado: number | null
    onSelecionarPonto: (id: number | null) => void
    overridesPontos: Record<number, ConfigPontoAcessoOverride>
    setOverridePonto: (idPonto: number, override: ConfigPontoAcessoOverride | null) => void
}

function rotuloPonto(ponto: PontoAcessoConfiguravel): string {
    return ponto.numero?.trim() || ponto.codigo
}

export default function SecaoCheckoutKiosk({ modos, form, idConfig, pontos, idPontoSelecionado, onSelecionarPonto, overridesPontos, setOverridePonto }: SecaoCheckoutKioskProps) {
    // Criação de um totem de checkout sem sair da aba (a lista abaixo depende dela).
    const [criandoPonto, setCriandoPonto] = useState(false)

    const pontoSelecionado = pontos.find((p) => p.id === idPontoSelecionado) ?? null
    const configAtual: ConfigPontoAcessoOverride = (pontoSelecionado ? overridesPontos[pontoSelecionado.id] ?? pontoSelecionado.configuracao : null) ?? {}

    function ler(chave: ChaveConfigPontoAcesso): unknown {
        return chave in configAtual ? configAtual[chave] : form[chave]
    }

    function escrever(chave: ChaveConfigPontoAcesso, valor: unknown): void {
        if (!pontoSelecionado) return
        setOverridePonto(pontoSelecionado.id, { ...configAtual, [chave]: valor } as ConfigPontoAcessoOverride)
    }

    const impressao = (ler('totemCheckoutImpressao') as string | undefined) ?? 'NFCE'
    const notaCheckout = (ler('totemCheckoutNota') as string | undefined) ?? 'PERGUNTAR'
    const perguntaCpf = ler('totemPerguntarCpfNota') !== false
    const comprovante = ler('totemImprimirComprovanteTef') !== false

    return (
        <GateModo modo="totemCheckout" modos={modos}>
            <InfoModo
                itens={[
                    <>
                        Este modo <strong>não cria pedido</strong>: ele fecha uma conta que já existe. O cliente
                        informa a comanda ou mesa, o totem busca o que foi consumido no Sischef e recebe o
                        pagamento.
                    </>,
                    <>
                        Itens adicionados aqui entram na <strong>mesma conta</strong>, no PDV de comanda/mesa —
                        não viram um pedido separado.
                    </>,
                    <>
                        O pagamento é feito no <strong>TEF do próprio totem</strong> e baixa a conta no Sischef.
                        Comprovante e NFC-e saem na impressora acoplada a este aparelho, escolhida nele mesmo.
                    </>,
                ]}
            />

            {/* Pontos de acesso*/}
            <Secao
                icone={<QrCode2Icon />}
                titulo="Pontos de acesso"
                descricao="Totens de checkout (fechamento de comanda). Abra a URL no navegador do aparelho (modo kiosk)."
                acoes={<BotaoGerenciarPontosDeAcesso idConfig={idConfig} tipos={['TOTEM_CHECKOUT']} />}
            >
                <PontosDeAcessoCard idConfig={idConfig} tipos={['TOTEM_CHECKOUT']} />
            </Secao>

            {/* Seleção de totem*/}
            <Secao
                icone={<PointOfSaleIcon />}
                titulo="Totem a configurar"
                descricao="Escolha o totem para editar suas configurações. Identificador, taxa de serviço e imagens são por aparelho; a impressora e o PIN ficam no próprio equipamento."
            >
                {pontos.length === 0 ? (
                    <Alert
                        severity="info"
                        action={
                            <Button color="inherit" size="small" startIcon={<AddIcon />} onClick={() => setCriandoPonto(true)}>
                                Criar
                            </Button>
                        }
                    >
                        Nenhum totem de checkout cadastrado. Crie um ponto de acesso do tipo{' '}
                        <strong>Totem Checkout</strong> para configurá-lo.
                    </Alert>
                ) : (
                    <TextField
                        select
                        value={idPontoSelecionado == null ? '' : String(idPontoSelecionado)}
                        onChange={(e) => onSelecionarPonto(e.target.value ? Number(e.target.value) : null)}
                        sx={{ minWidth: { sm: 340 } }}
                        helperText="Selecione um totem para ver e editar as configurações dele."
                        slotProps={{
                            select: {
                                displayEmpty: true,
                                renderValue: (valor) => {
                                    const ponto = pontos.find((p) => String(p.id) === valor)
                                    return ponto ? rotuloPonto(ponto) : 'Selecione um totem'
                                }
                            }
                        }}
                    >
                        {pontos.map((p) => (
                            <MenuItem key={p.id} value={String(p.id)}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <span>{rotuloPonto(p)}</span>
                                    {!p.ativo && <Chip size="small" label="Inativo" />}
                                    {p.id in overridesPontos && <Chip size="small" color="warning" label="Não salvo" />}
                                </Stack>
                            </MenuItem>
                        ))}
                    </TextField>
                )}
            </Secao>

            {pontoSelecionado && (
                <>
                    {/* Imagens da tela inicial deste totem */}
                    <Secao
                        icone={<SlideshowIcon />}
                        titulo="Tela inicial"
                        descricao="Imagens de fundo da tela inicial (a de 'faça aqui seu pagamento') deste totem."
                    >
                        <UploadImagensTotem
                            valor={ler('imagensTotemCheckout') as string[] | null}
                            onChange={(v) => escrever('imagensTotemCheckout', v)}
                            ajuda="Até 5 imagens que passam em carrossel no fundo da tela inicial deste totem, com a chamada 'faça aqui seu pagamento' por cima. Sem imagens, a tela inicial fica com a logo do Sischef e o bloco colorido padrão."
                        />
                    </Secao>

                    {/* Identificador principal */}
                    <Secao
                        icone={<TableRestaurantIcon />}
                        titulo="Identificador principal"
                        descricao="Modelo de atendimento principal para pedidos de comanda e mesa."
                    >
                        <Grid container spacing={3} alignItems="flex-start">
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    select
                                    label="Identificador principal"
                                    fullWidth
                                    value={(ler('totemCheckoutIdentificador') as string) ?? 'COMANDA'}
                                    onChange={(e) => escrever('totemCheckoutIdentificador', e.target.value)}
                                >
                                    <MenuItem value="COMANDA">Comanda</MenuItem>
                                    <MenuItem value="MESA">Mesa</MenuItem>
                                </TextField>
                            </Grid>
                        </Grid>
                    </Secao>

                    {/* Taxa de serviço */}
                    <Secao
                        icone={<PaidIcon />}
                        titulo="Taxa de serviço"
                        descricao="A taxa de serviço é opcional. Aqui você define o percentual cobrado neste totem e se o cliente pode retirá-la da conta ou se será necessário chamar um atendente."
                    >
                        <Grid container spacing={3} alignItems="flex-start">
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    label="Percentual da taxa"
                                    fullWidth
                                    type="number"
                                    value={(ler('taxaServicoPercentual') as string) ?? ''}
                                    onChange={(e) => escrever('taxaServicoPercentual', e.target.value || null)}
                                    slotProps={{
                                        htmlInput: { min: 0, max: 100, step: '0.01' },
                                        input: { endAdornment: <InputAdornment position="end">%</InputAdornment> },
                                    }}
                                    helperText="Em branco usa o percentual configurado no Sischef. Zero não cobra taxa neste totem."
                                />
                            </Grid>

                            <Grid size={{ xs: 12, md: 6 }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={(ler('taxaServicoDispensavel') as boolean) ?? true}
                                            onChange={(_e, v) => escrever('taxaServicoDispensavel', v)}
                                        />
                                    }
                                    label={
                                        <Typography variant="body2" color="text.secondary" sx={{ ml: 1.5 }}>
                                            Permitir que o cliente retire a taxa de serviço no totem. A escolha é
                                            gravada no pedido e o PDV é avisado na hora.
                                        </Typography>
                                    }
                                    sx={{ alignItems: 'flex-start', m: 0 }}
                                />
                            </Grid>
                        </Grid>
                    </Secao>

                    {/* Impressão ao fim do pagamento, por totem — mesmo eixo da aba TOTEM, sem a senha. */}
                    <Secao
                        icone={<ReceiptLongIcon />}
                        titulo="Impressão do pagamento"
                        descricao="O que o totem imprime quando o cliente termina de pagar a conta. A NFC-e só é emitida se a empresa tiver a NF-e ativada no Sischef."
                    >
                        <Grid container spacing={2} alignItems="flex-start">
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    select
                                    label="Ao concluir o pagamento"
                                    fullWidth
                                    value={impressao}
                                    onChange={(e) => escrever('totemCheckoutImpressao', e.target.value)}
                                    helperText="Com NFC-e, o totem pergunta o CPF antes de emitir."
                                >
                                    <MenuItem value="NENHUMA">Não imprimir</MenuItem>
                                    <MenuItem value="RESUMO">Resumo da conta</MenuItem>
                                    <MenuItem value="NFCE">Nota fiscal (NFC-e)</MenuItem>
                                </TextField>
                            </Grid>

                            {impressao === 'NFCE' && (
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <CampoUsuarioNfce
                                        idConfig={idConfig}
                                        valor={(ler('totemUsuarioNfce') as string) ?? null}
                                        onChange={(username) => escrever('totemUsuarioNfce', username)}
                                    />
                                </Grid>
                            )}

                            {impressao === 'NFCE' && (
                                <Grid size={12}>
                                    <Typography id="impressao-nota-checkout" variant="subtitle2" sx={{ mb: 1 }}>
                                        Impressão da nota
                                    </Typography>

                                    <RadioGroup
                                        aria-labelledby="impressao-nota-checkout"
                                        value={notaCheckout}
                                        onChange={(_e, valor) => escrever('totemCheckoutNota', valor)}
                                    >
                                        <FormControlLabel
                                            value="SEMPRE"
                                            control={<Radio />}
                                            sx={{ alignItems: 'flex-start', mb: 1 }}
                                            label={
                                                <Box sx={{ pt: 0.75 }}>
                                                    <Typography>Imprimir sempre</Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        A nota sai no papel sempre que a conta é fechada neste totem.
                                                    </Typography>
                                                </Box>
                                            }
                                        />
                                        <FormControlLabel
                                            value="PERGUNTAR"
                                            control={<Radio />}
                                            sx={{ alignItems: 'flex-start' }}
                                            label={
                                                <Box sx={{ pt: 0.75 }}>
                                                    <Typography>Perguntar ao cliente se quer imprimir</Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        A nota é emitida do mesmo jeito; o cliente só escolhe se ela sai no papel.
                                                    </Typography>
                                                </Box>
                                            }
                                        />
                                    </RadioGroup>

                                    <OpcaoImpressaoTotem
                                        marcada={perguntaCpf}
                                        aoMudar={(v) => escrever('totemPerguntarCpfNota', v)}
                                        titulo="Perguntar o CPF na nota"
                                        ajuda="Desmarcado, o totem não pergunta e a nota sai sem CPF."
                                    />
                                </Grid>
                            )}

                            <Grid size={12}>
                                <OpcaoImpressaoTotem
                                    marcada={comprovante}
                                    aoMudar={(v) => escrever('totemImprimirComprovanteTef', v)}
                                    titulo="Imprimir comprovante do cartão"
                                    ajuda="A via do cliente da maquininha, a cada pagamento feito neste totem."
                                />
                            </Grid>

                            {(impressao !== 'NENHUMA' || comprovante) && (
                                <Grid size={12}>
                                    <AvisoPinDispositivo
                                        comoAbrir
                                        titulo="A impressora é configurada no próprio totem"
                                        intro="Escolha a impressora na tela de configuração local do aparelho, protegida por PIN."
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </Secao>

                    {/* Balança (autopesagem) */}
                    <Secao
                        icone={<ScaleIcon />}
                        titulo="Balança"
                        descricao="Só funciona em totens com Windows. Ligue apenas se este totem tem balança configurada."
                    >
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={(ler('totemBalancaAtiva') as boolean) ?? false}
                                    onChange={(_e, v) => escrever('totemBalancaAtiva', v)}
                                />
                            }
                            label={
                                <Stack>
                                    <Typography variant="body1" sx={{ ml: 1.5 }}>
                                        Usar balança neste totem.
                                    </Typography>

                                    <Typography variant="body2" color="text.secondary" sx={{ ml: 1.5 }}>
                                        O cliente coloca o item na balança e escolhe o
                                        produto pesado, que entra na conta pelo peso lido.
                                    </Typography>
                                </Stack>
                            }
                            sx={{ alignItems: 'flex-start', m: 0 }}
                        />

                        {((ler('totemBalancaAtiva') as boolean) ?? false) && (
                            <Alert severity="warning" sx={{ mt: 2 }}>
                                <AlertTitle>A balança só funciona em totens com Windows</AlertTitle>
                                O navegador e o totem Android não falam com a balança: quem lê o peso é o{' '}
                                <strong>Totem de Autoatendimento para Windows</strong> instalado no aparelho,
                                sempre na versão mais recente. Baixe em{' '}
                                <Link href={URL_DOWNLOADS} target="_blank" rel="noopener">
                                    downloads.sischef.com
                                </Link>
                                . Em totem Android a balança não aparece, mesmo ligada aqui.
                            </Alert>
                        )}
                    </Secao>

                    {/* Leitor de código de barras no resumo do pedido */}
                    <Secao
                        icone={<QrCodeScannerIcon />}
                        titulo="Leitor de código de barras"
                        descricao="Ligue apenas se este totem tem leitor de código de barras."
                    >
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={(ler('totemCheckoutLeitorCodigoBarras') as boolean) ?? false}
                                    onChange={(_e, v) => escrever('totemCheckoutLeitorCodigoBarras', v)}
                                />
                            }
                            label={
                                <Stack>
                                    <Typography variant="body1" sx={{ ml: 1.5 }}>
                                        Usar leitor de código de barras neste totem.
                                    </Typography>

                                    <Typography variant="body2" color="text.secondary" sx={{ ml: 1.5 }}>
                                        No resumo do pedido, o cliente passa o produto no leitor e ele
                                        entra direto na conta. A tela mostra ao cliente que dá para fazer isso.
                                    </Typography>
                                </Stack>
                            }
                            sx={{ alignItems: 'flex-start', m: 0 }}
                        />
                    </Secao>
                </>
            )}

            <DialogPontosAcesso
                aberto={criandoPonto}
                aoFechar={() => setCriandoPonto(false)}
                idConfig={idConfig}
                tipos={['TOTEM_CHECKOUT']}
                criarAoAbrir
            />
        </GateModo>
    )
}
